import { Modal, Input, Tag, Typography, Divider, message, Alert } from "antd";
import { TbCircleCheck, TbCircleX } from "react-icons/tb";
import { useState, useEffect } from "react";
import { T_Notification } from "dtos/notifications.dto";
import { ApiService } from "api/api.service";
import { cardActivityService } from "api/card-activity.service";

const { Text } = Typography;

const ResolveEmailModal = ({
  open,
  onClose,
  notification,
  onResolved,
}: {
  open: boolean;
  onClose: () => void;
  notification: T_Notification | null;
  onResolved: () => void;
}) => {
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const ctx = notification?.context;

  const activityQuery = cardActivityService.useGetCardActivity(
    open && ctx?.cardActivity ? { activityId: ctx.cardActivity } : undefined,
  );

  const activity = activityQuery.data?.result;

  const resolveMutation = ApiService.notification.useResolveCardActivity();

  // Reset overrides when modal opens with a new notification
  useEffect(() => {
    if (!open) {
      setOverrides({});
    }
  }, [open]);

  const handleSend = () => {
    if (!activity || !ctx?.cardActivity) return;
    const missing = activity.unresolvedVariables.filter((v) => !overrides[v]?.trim());

    if (missing.length > 0) {
      return message.warning(`Please fill all required fields: ${missing.join(", ")}`);
    }

    resolveMutation.mutate(
      {
        activityId: ctx.cardActivity,
        applicationId: ctx.application!,
        templateId: ctx.template!,
        loanId: ctx.loan,
        overrides,
      },
      {
        onSuccess: () => {
          message.success("Email sent successfully");
          onResolved();
          onClose();
        },
        onError: (err: any) => {
          message.error(err?.response?.data?.message ?? "Failed to send email");
        },
      },
    );
  };

  const isLoading = activityQuery.isLoading;

  return (
    <Modal
      title="Fill & Send Email"
      open={open}
      onCancel={onClose}
      onOk={handleSend}
      confirmLoading={resolveMutation.isPending}
      okText="Send Email"
      centered
      okButtonProps={{
        disabled: isLoading || !activity,
      }}
    >
      {isLoading ? (
        <div className="py-6 flex justify-center">
          <Text type="secondary">Loading activity...</Text>
        </div>
      ) : !activity ? (
        <Alert type="error" showIcon message="Could not load activity details. Please try again." />
      ) : (
        <>
          <Alert
            showIcon
            className="mb-4"
            type="warning"
            message={`Template "${activity.templateName}" could not be sent automatically. Fill in the missing variables below.`}
          />

          {/* Resolved variables — read only */}
          {activity.resolvedVariables.length > 0 && (
            <>
              <Text type="secondary" className="text-xs uppercase tracking-wide">
                Resolved
              </Text>
              <div className="flex flex-wrap gap-2 mt-2">
                {activity.resolvedVariables.map((v) => (
                  <Tag key={v} icon={<TbCircleCheck className="inline mr-1" />} color="success">
                    {v}
                  </Tag>
                ))}
              </div>
              <Divider className="my-4" />
            </>
          )}

          {/* Unresolved variables — editable */}
          {activity.unresolvedVariables.length > 0 && (
            <>
              <Text type="secondary" className="text-xs uppercase tracking-wide">
                Requires Manual Input
              </Text>
              <div className="flex flex-col gap-3 mt-3">
                {activity.unresolvedVariables.map((v) => (
                  <div key={v} className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <TbCircleX className="text-red-500" />
                      <Text className="text-sm font-medium">{v}</Text>
                    </div>
                    <Input
                      placeholder={`Enter value for ${v}`}
                      value={overrides[v] ?? ""}
                      onChange={(e) =>
                        setOverrides((prev) => ({
                          ...prev,
                          [v]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default ResolveEmailModal;
